import {
	CopyOutlined,
	DeleteOutlined,
	EditOutlined,
	MoreOutlined,
	SyncOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Dropdown, Modal, message, Popconfirm, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { EmptyState } from "@/shared/components/EmptyState";
import { StatusTag } from "@/shared/components/StatusTag";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { resolveImageUrl } from "@/shared/utils/resolveImageUrl";
import { tablePagination } from "@/shared/utils/tablePagination";
import {
	useDeleteFaceEnrollment,
	useFaceEnrollmentList,
	useSyncFaceEnrollment,
} from "../hooks/useFaceEnrollment";
import { useFaceEnrollmentStore } from "../store/face-enrollment.store";
import type { FaceEnrollment } from "../types";

interface FaceEnrollmentTableProps {
	organizationId: string;
	organizationName?: string;
	page: number;
	limit: number;
	onPageChange: (page: number) => void;
}

export function FaceEnrollmentTable({
	organizationId,
	organizationName,
	page,
	limit,
	onPageChange,
}: FaceEnrollmentTableProps) {
	const { data, isLoading } = useFaceEnrollmentList({
		organizationId,
		page,
		limit,
	});
	const { mutate: remove } = useDeleteFaceEnrollment();
	const { mutate: sync } = useSyncFaceEnrollment();
	const openEditModal = useFaceEnrollmentStore((s) => s.openEditModal);
	const [messageApi, contextHolder] = message.useMessage();
	const [previewImage, setPreviewImage] = useState<string | null>(null);
	const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());

	const handleSync = (id: string) => {
		setSyncingIds((prev) => new Set(prev).add(id));
		sync(id, {
			onSettled: () => {
				setSyncingIds((prev) => {
					const next = new Set(prev);
					next.delete(id);
					return next;
				});
			},
		});
	};

	const handleCopyHikvisionId = async (id: string) => {
		try {
			await navigator.clipboard.writeText(id);
			messageApi.success("Hikvision ID nusxalandi");
		} catch {
			messageApi.error("Nusxalashda xatolik yuz berdi");
		}
	};

	const columns: ColumnsType<FaceEnrollment> = [
		{
			title: "№",
			key: "index",
			width: 55,
			align: "center",
			render: (_1: unknown, _2: unknown, index: number) => formatTableIndex(page, limit, index),
		},
		{
			title: "Ega",
			key: "owner",
			render: (_: unknown, record: FaceEnrollment) => {
				const ownerName =
					record.ownerName ||
					record.staffName ||
					record.staff?.fullname ||
					"Noma'lum foydalanuvchi";
				const ownerImage = record.faceImageUrl || record.faceImage?.url || undefined;
				const resolvedImage = resolveImageUrl(ownerImage);

				return (
					<div className="flex items-center gap-2">
						{resolvedImage ? (
							<button
								type="button"
								onClick={() => setPreviewImage(resolvedImage)}
								className="p-0 border-none bg-transparent cursor-pointer"
								// aria-label={`${ownerName} rasmini ko'rish`}
							>
								<Avatar
									size={40}
									style={{ background: "#1D2026", color: "#fff", fontSize: 13 }}
									src={resolvedImage}
								>
									{ownerName?.charAt(0)?.toUpperCase() ?? "X"}
								</Avatar>
							</button>
						) : (
							<Avatar size={40} style={{ background: "#1D2026", color: "#fff", fontSize: 13 }}>
								{ownerName?.charAt(0)?.toUpperCase() ?? "X"}
							</Avatar>
						)}
						<div>
							<div className="font-medium text-sm">{ownerName}</div>
							<div className="text-xs text-ink-soft uppercase tracking-widest">
								{record.ownerType ?? "Xodim"}
							</div>
						</div>
					</div>
				);
			},
		},
		{
			title: "Hikvision ID",
			dataIndex: "hikvisionFaceId",
			key: "hikvisionFaceId",
			width: 150,
			render: (id: string) => (
				<div className="flex items-center gap-2">
					<Tag color="blue" className="font-mono text-xs">
						{id}
					</Tag>
					<Button
						type="text"
						size="small"
						icon={<CopyOutlined />}
						aria-label="Hikvision ID ni nusxalash"
						onClick={() => handleCopyHikvisionId(id)}
					/>
				</div>
			),
		},
		{
			title: "Sana",
			dataIndex: "enrolledAt",
			key: "enrolledAt",
			width: 130,
			render: (date: string) =>
				new Date(date).toLocaleDateString("uz-UZ", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
				}),
		},
		{
			title: "Holat",
			dataIndex: "isActive",
			key: "isActive",
			width: 90,
			render: (isActive: boolean) =>
				isActive ? (
					<StatusTag tone="success">Faol</StatusTag>
				) : (
					<StatusTag tone="default">Nofaol</StatusTag>
				),
		},
		{
			title: "Amallar",
			key: "actions",
			width: 80,
			align: "center",
			render: (_: unknown, record: FaceEnrollment) => (
				<Dropdown
					menu={{
						items: [
							{
								key: "sync",
								icon: <SyncOutlined spin={syncingIds.has(record.id)} />,
								label: "sinxronlashtirish",
								onClick: () => handleSync(record.id),
							},
							{
								key: "edit",
								icon: <EditOutlined />,
								label: "Tahrirlash",
								onClick: () => openEditModal(record),
							},
							{
								key: "delete",
								danger: true,
								label: (
									<button
										type="button"
										className="bg-transparent border-none p-0 m-0 cursor-pointer text-inherit text-left w-full outline-none"
										onClick={(e) => {
											e.stopPropagation();
										}}
									>
										<Popconfirm
											title="O'chirish"
											description="Rostdan ham o'chirmoqchimisiz?"
											onConfirm={() => remove(record.id)}
											okText="Ha"
											cancelText="Yo'q"
											okButtonProps={{ danger: true }}
										>
											<span className="flex items-center gap-2">
												<DeleteOutlined />
												<span>O'chirish</span>
											</span>
										</Popconfirm>
									</button>
								),
							},
						],
					}}
					trigger={["click"]}
					placement="bottomRight"
				>
					<Button type="text" icon={<MoreOutlined />} size="small" />
				</Dropdown>
			),
		},
	];

	return (
		<>
			{contextHolder}
			<Table
				title={
					organizationName
						? () => (
								<div className="flex items-center gap-2">
									<span className="text-ink-soft text-sm">Tashkilot:</span>
									<Tag>{organizationName}</Tag>
								</div>
							)
						: undefined
				}
				columns={columns}
				dataSource={data?.data?.items || []}
				loading={isLoading}
				rowKey="id"
				pagination={tablePagination({
					page,
					limit,
					total: data?.data?.meta?.total,
					onChange: onPageChange,
					sizeChanger: false,
				})}
				size="middle"
				locale={{
					emptyText: (
						<EmptyState
							description={
								organizationName ? (
									<span>
										Yuz ma'lumotlari yo'q — <strong>{organizationName}</strong>
									</span>
								) : (
									"Ma'lumot yo'q"
								)
							}
						/>
					),
				}}
			/>
			<Modal
				open={Boolean(previewImage)}
				onCancel={() => setPreviewImage(null)}
				footer={null}
				width={720}
				destroyOnHidden
			>
				{previewImage ? (
					<img
						src={previewImage}
						alt="Face enrollment preview"
						style={{
							width: "100%",
							maxHeight: "72vh",
							objectFit: "contain",
							borderRadius: 8,
						}}
					/>
				) : null}
			</Modal>
		</>
	);
}
